import React, {FC} from 'react';

import {Box, Checkbox, Typography} from '@mui/material';
import {t} from 'i18next';
import {If, Else, Then, When} from 'react-if';

import {AlcoDeliveryInfoIcon} from 'assets/icons/AlcoDeliveryInfoIcon';
import {ChevronRight} from 'assets/icons/chevronRight';
import MakroProIcon from 'assets/icons/makro-pro';
import NewSellerIcon from 'assets/icons/newSeller';
import {SellerIcon} from 'assets/icons/seller';
import theme from 'config/theme';
import {useAppMode} from 'hooks/useAppMode';
import {formatPriceWithCurrency} from 'utils/currency';

import {BasketDeliveryFee} from './BasketDeliveryFee';
import {
  BasketDeliveryInfo1P,
  BasketDeliveryInfo3P as BasketDeliveryInfo,
  BasketDeliveryInfoMakroFleet
} from './BasketDeliveryInfo';
import {
  StyledAlcoMessageWrapper,
  StyledBasketSellerHeader,
  StyledBasketSellerName,
  StyledBasketLabel,
  StyledShopLogo
} from './styles';

interface BasketSellerHeaderProps {
  onClick: () => void;
  subtotal: number;
  priceUnit: string;
  isSelectedAllBasketItems: boolean;
  isAllItemsFromThisSectionSelected: boolean;
  isReactNativeView?: boolean;
}

export interface BasketSellerHeader1PProps extends BasketSellerHeaderProps {
  currentShippingFee: number;
  defaultShippingFee: number;
  deliveryBy: string;
  onlyBulkyItems: boolean;
  onlyPreOrderItems: boolean;
  isFlexibleBasketEnabled: boolean;
  onSelectSection: (e: any) => void;
  description?: string;
}

export interface BasketSellerHeader3PProps extends BasketSellerHeaderProps {
  deliveryBy: string;
  sellerName: string;
  shippingFee: number;
  originalShippingFee?: number;
  isFlexibleBasketEnabled: boolean;
  onSelectSection: (e: any) => void;
  showDeliveryFee?: boolean;
  shopLogo?: string;
}

export interface BasketSellerHeaderMakroFleetProps extends BasketSellerHeaderProps {
  sellerName: string;
  shippingFee: number;
  description: string;
  isFlexibleBasketEnabled: boolean;
  onSelectSection: (e: any) => void;
}

export interface BasketSellerHeaderBulkyProps {
  title: string;
  onClick: () => void;
  deliveryBy: string;
  onlyBulkyItems: boolean;
  only1PxBulkyItems: boolean;
  isFlexibleBasketEnabled: boolean;
  onSelectSection: (e: any) => void;
  is1PSeller?: boolean;
  is1PX?: boolean;
  isSelectedAllBasketItems: boolean;
  isAllItemsFromThisSectionSelected: boolean;
  isReactNativeView?: boolean;
}
export interface BasketSellerHeaderPreOrderProps {
  onClick: () => void;
  title: string;
  deliveryBy: string;
  onlyPreOrderItems: boolean;
  isFlexibleBasketEnabled: boolean;
  onSelectSection: (e: any) => void;
  isSelectedAllBasketItems: boolean;
  isAllItemsFromThisSectionSelected: boolean;
  isReactNativeView?: boolean;
}

export interface BasketSellerHeaderAlcoProps {
  title: string;
  onClick: () => void;
  message: string;
  isFlexibleBasketEnabled: boolean;
  minimumQuantityError?: string;
  onSelectSection: (e: any) => void;
  isSelectedAllBasketItems: boolean;
  isAllItemsFromThisSectionSelected: boolean;
  onClickDeliveryInfo: () => void;
  isUnavailable?: boolean;
  isReactNativeView?: boolean;
}

const checkBoxComponent = (props: any) => {
  return (
    <Box>
      <Checkbox
        name="isDefault"
        disabled={props.disabled}
        defaultChecked={props.defaultChecked}
        data-test-id={`${props.sectionName}_seller_header_checkbox`}
        sx={{
          '&.Mui-disabled': {color: theme.palette.primary.light},
          color: '#C7C7C7',
          '&.Mui-checked': {
            color: 'primary'
          },
          height: 'fit-content',
          padding: 0
        }}
        {...props}
      />
    </Box>
  );
};

// In flexible basket this header will not render for other than 1p e.g bulky section
export const BasketSellerHeader1P: FC<BasketSellerHeader1PProps> = (props) => {
  const {isRegular} = useAppMode();
  return (
    <StyledBasketSellerHeader
      isReactNativeView={props.isReactNativeView}
      isFlexibleBasketEnabled={props.isFlexibleBasketEnabled}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center">
          <If condition={props.isFlexibleBasketEnabled}>
            <Then>
              {checkBoxComponent({
                defaultChecked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
                checked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
                onChange: props.onSelectSection,
                sectionName: '1p'
              })}
            </Then>
            <Else>
              <SellerIcon />
            </Else>
          </If>
          &nbsp;&nbsp;
          <MakroProIcon />
        </Box>
        <When condition={!props.isFlexibleBasketEnabled && isRegular}>
          <Box display="flex" alignItems="end" fontSize="0.8rem">
            <StyledBasketLabel>{t('label.subtotal')}:</StyledBasketLabel>
            &nbsp;
            <Typography fontSize="0.9rem" fontWeight={700}>
              {formatPriceWithCurrency(props.subtotal, props.priceUnit)}
            </Typography>
          </Box>
        </When>
      </Box>

      <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
        <If condition={props.onlyBulkyItems && props.deliveryBy}>
          <Then>
            <BasketDeliveryInfoMakroFleet
              message={props.deliveryBy}
              onClick={props.onClick}
              dataTestId={'basket_bulky_delivery_info'}
            />
          </Then>
          <Else>
            <If condition={props.onlyPreOrderItems && props.deliveryBy}>
              <Then>
                <BasketDeliveryInfo
                  message={props.deliveryBy}
                  onClick={props.onClick}
                  dataTestId={'basket_pre_order_delivery_info'}
                />
              </Then>
              <Else>
                <BasketDeliveryInfo1P onClick={props.onClick} message={props.description} />
              </Else>
            </If>
          </Else>
        </If>
        <When condition={!props.isFlexibleBasketEnabled && isRegular}>
          <Box display="flex" alignItems="flex-end">
            <StyledBasketLabel>{t('label.deliveryFee')}:</StyledBasketLabel>
            &nbsp;
            <BasketDeliveryFee
              currentShippingFee={props.currentShippingFee}
              defaultShippingFee={props.defaultShippingFee}
              priceUnit={props.priceUnit}
            />
          </Box>
        </When>
      </Box>
    </StyledBasketSellerHeader>
  );
};

export const BasketSellerHeader3P: FC<BasketSellerHeader3PProps> = (props) => {
  return (
    <StyledBasketSellerHeader
      isReactNativeView={props.isReactNativeView}
      isFlexibleBasketEnabled={props.isFlexibleBasketEnabled}
    >
      <Box display="flex" width="100%">
        <Box display="flex" alignItems="center">
          <When condition={props.isFlexibleBasketEnabled}>
            {checkBoxComponent({
              defaultChecked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
              checked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
              onChange: props.onSelectSection,
              sectionName: '3p'
            })}
          </When>
          &nbsp;&nbsp;
          {props.shopLogo ? (
            <StyledShopLogo>
              <img width="20px" height="20px" src={props.shopLogo} alt="seller logo" />
            </StyledShopLogo>
          ) : (
            <NewSellerIcon />
          )}
        </Box>
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          paddingLeft="8px"
        >
          <StyledBasketSellerName>{props.sellerName}</StyledBasketSellerName>
          <If condition={props.isFlexibleBasketEnabled}>
            <Then>
              <When condition={props.showDeliveryFee}>
                <Box display="flex" alignItems="center" justifyContent={'space-between'}>
                  <StyledBasketLabel>{t('label.deliveryFee')}:</StyledBasketLabel>
                  &nbsp;
                  <BasketDeliveryFee
                    currentShippingFee={props.shippingFee}
                    defaultShippingFee={props.originalShippingFee}
                    priceUnit={props.priceUnit}
                  />
                </Box>
              </When>
            </Then>
            <Else>
              <Box display="flex" alignItems="center" fontSize="0.8rem">
                <StyledBasketLabel>{t('label.subtotal')}:</StyledBasketLabel>
                &nbsp;
                <Typography fontSize="0.9rem" fontWeight={700}>
                  {formatPriceWithCurrency(props.subtotal, props.priceUnit)}
                </Typography>
              </Box>
            </Else>
          </If>
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
        <BasketDeliveryInfo message={props.deliveryBy} onClick={props.onClick} />
        <When condition={!props.isFlexibleBasketEnabled}>
          <Box display="flex" alignItems="center">
            <StyledBasketLabel>{t('label.deliveryFee')}:</StyledBasketLabel>
            &nbsp;
            <BasketDeliveryFee currentShippingFee={props.shippingFee} priceUnit={props.priceUnit} />
          </Box>
        </When>
      </Box>
    </StyledBasketSellerHeader>
  );
};

export const BasketSellerHeaderMakroFleet: FC<BasketSellerHeaderMakroFleetProps> = (props) => {
  return (
    <StyledBasketSellerHeader
      isReactNativeView={props.isReactNativeView}
      isFlexibleBasketEnabled={props.isFlexibleBasketEnabled}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <If condition={props.isFlexibleBasketEnabled}>
            <Then>
              {' '}
              {checkBoxComponent({
                defaultChecked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
                checked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
                onChange: props.onSelectSection,
                sectionName: 'makro_fleet'
              })}
              &nbsp;&nbsp;
              <MakroProIcon />
              &nbsp;&nbsp;
            </Then>
            <Else>
              <SellerIcon />
            </Else>
          </If>
          &nbsp;&nbsp;
          <StyledBasketSellerName>{props.sellerName}</StyledBasketSellerName>
        </Box>
        <Box display="flex" alignItems="center" fontSize="0.8rem">
          <StyledBasketLabel>{t('label.subtotal')}:</StyledBasketLabel>
          &nbsp;
          <Typography fontSize="0.9rem" fontWeight={700}>
            {formatPriceWithCurrency(props.subtotal, props.priceUnit)}
          </Typography>
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
        <BasketDeliveryInfoMakroFleet message={props.description} onClick={props.onClick} />
        <Box display="flex" alignItems="center">
          <StyledBasketLabel>{t('label.deliveryFee')}:</StyledBasketLabel>
          &nbsp;
          <BasketDeliveryFee currentShippingFee={props.shippingFee} priceUnit={props.priceUnit} />
        </Box>
      </Box>
    </StyledBasketSellerHeader>
  );
};

export const BasketSellerHeaderBulky: FC<BasketSellerHeaderBulkyProps> = (props) => {
  return (
    <StyledBasketSellerHeader
      isReactNativeView={props.isReactNativeView}
      enableTopBorder={props.onlyBulkyItems || props.only1PxBulkyItems}
      isFlexibleBasketEnabled={props.isFlexibleBasketEnabled}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <When condition={props.isFlexibleBasketEnabled}>
            {checkBoxComponent({
              defaultChecked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
              checked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
              onChange: props.onSelectSection,
              sectionName: 'bulky'
            })}
            &nbsp;&nbsp;
            <MakroProIcon />
            &nbsp;&nbsp;
          </When>
          <StyledBasketSellerName>{props.title}</StyledBasketSellerName>
        </Box>
      </Box>
      <When
        condition={
          (!props.onlyBulkyItems && !props.only1PxBulkyItems && !props.is1PSeller) || props.isFlexibleBasketEnabled
        }
      >
        <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
          <BasketDeliveryInfoMakroFleet
            message={props.deliveryBy}
            onClick={props.onClick}
            dataTestId={'basket_bulky_delivery_info'}
          />
        </Box>
      </When>
    </StyledBasketSellerHeader>
  );
};

export const BasketSellerHeaderPreOrder: FC<BasketSellerHeaderPreOrderProps> = (props) => {
  return (
    <StyledBasketSellerHeader
      isReactNativeView={props.isReactNativeView}
      enableTopBorder={props.onlyPreOrderItems}
      isFlexibleBasketEnabled={props.isFlexibleBasketEnabled}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <When condition={props.isFlexibleBasketEnabled}>
            {checkBoxComponent({
              defaultChecked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
              checked: props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
              onChange: props.onSelectSection,
              sectionName: 'pre_order'
            })}
            &nbsp;&nbsp;
            <MakroProIcon />
            &nbsp;&nbsp;
          </When>
          <StyledBasketSellerName>{props.title}</StyledBasketSellerName>
        </Box>
      </Box>

      <When condition={props.isFlexibleBasketEnabled || !props.onlyPreOrderItems}>
        <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
          <BasketDeliveryInfo
            message={props.deliveryBy}
            onClick={props.onClick}
            dataTestId={'basket_pre_order_delivery_info'}
          />
        </Box>
      </When>
    </StyledBasketSellerHeader>
  );
};

export const BasketSellerHeaderAlco: FC<BasketSellerHeaderAlcoProps> = (props) => (
  <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
    <StyledBasketSellerHeader
      isReactNativeView={props.isReactNativeView}
      isFlexibleBasketEnabled={props.isFlexibleBasketEnabled}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <When condition={props.isFlexibleBasketEnabled}>
            {checkBoxComponent({
              defaultChecked: props.isUnavailable
                ? false
                : props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
              checked: props.isUnavailable
                ? false
                : props.isAllItemsFromThisSectionSelected || props.isSelectedAllBasketItems,
              onChange: props.onSelectSection,
              disabled: props.isUnavailable,
              sectionName: 'alco'
            })}
            &nbsp;&nbsp;
            <MakroProIcon />
            &nbsp;&nbsp;
          </When>
          <StyledBasketSellerName>{props.title}</StyledBasketSellerName>
        </Box>
        <When condition={!!props.minimumQuantityError && !props.isFlexibleBasketEnabled}>
          <Typography fontWeight={400} fontSize={12} color="grey.400">
            {props.minimumQuantityError}
          </Typography>
        </When>
      </Box>
      <If condition={props.isFlexibleBasketEnabled}>
        <Then>
          <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
            <BasketDeliveryInfo1P
              onClick={props.onClickDeliveryInfo}
              message={props?.message}
              dataTestId={'basket_alco_delivery_info'}
            />
          </Box>
        </Then>
        <Else>
          <Box display="flex" justifyContent="space-between" width="100%" alignItems="end">
            <StyledAlcoMessageWrapper onClick={props.onClick}>
              <Typography color="crimson" fontSize={12} fontWeight={400}>
                {props.message}
              </Typography>
              <ChevronRight />
            </StyledAlcoMessageWrapper>
          </Box>
        </Else>
      </If>
    </StyledBasketSellerHeader>
    <Box sx={{maxHeight: '20px', paddingRight: 2, cursor: 'pointer'}} onClick={props.onClick}>
      {props.isFlexibleBasketEnabled && <AlcoDeliveryInfoIcon />}
    </Box>
  </Box>
);

BasketSellerHeader1P.displayName = 'BasketSellerHeader1P';
BasketSellerHeader3P.displayName = 'BasketSellerHeader3P';
